"use client"

import { TSP_CONFIG } from "../config"
import {
  Calculator,
  ChartNetwork,
  ChartNoAxesCombined,
  ClipboardList,
  Component,
  Crosshair,
  Dices,
  Gauge,
  Mail,
  Medal,
  Newspaper,
  PieChart,
  Plane,
  Scan,
  Swords,
  Trophy,
  UserCog,
  User as UserIcon,
  Users,
} from "lucide-react"
import { useEffect, useMemo } from "react"
import { LiaFlagUsaSolid } from "react-icons/lia"
import { PiRanking } from "react-icons/pi"
import { TbSkiJumping } from "react-icons/tb"

import { newFrontendURL } from "@/lib/new-url"

import AppSideBar, {
  type SidebarSettings,
  type settingsHeader,
} from "@/components/nango/common/layout/sidebar"
import CountryFlag from "@/components/nango/tsp/country-flag"

import { ALL_SEASONS } from "@/app/tsp/logged-in/ski-analysis/types"

import { UpgradeSidebarCard } from "./upgrade-sidebar-card"
import signOut from "@/actions/signOut"
import { Athlete } from "@/nango/types/apps.tsp/athlete/Athlete"
import { UserProfile } from "@/nango/types/apps.tsp/user_profile/UserProfile"
import { User } from "@/nango/types/nango.user/user/User"

export default function LayoutClientSide({
  children,
  user,
  profile,
  favorites = [],
}: {
  children: React.ReactNode
  user: User | undefined
  profile: UserProfile | null
  favorites?: Athlete[]
}) {
  const isUsa = profile?.is_usa ?? false
  const isPro = (profile?.is_athlete ?? false) || (profile?.is_coach ?? false)
  const isStaff = user?.is_staff ?? false

  const settings = useMemo((): SidebarSettings & { header: settingsHeader } => {
    const headerOptions = favorites.map((a) => ({
      id: a.id,
      title: `${a.last_name} ${a.first_name}`,
      subtitle: a.country,
      fullText: `${a.last_name} ${a.first_name} · ${a.country}`,
      icon: (
        <CountryFlag
          code={a.country}
          className="h-8 w-8 rounded-full object-cover"
        />
      ),
    }))

    return {
      header: {
        name: "Athlete",
        options: headerOptions,
        addOption: {
          text: "Browse athletes",
          href: newFrontendURL(TSP_CONFIG, "/athletes/", true),
        },
      },
      sections: [
        {
          title: "General",
          subSections: [
            {
              title: "Dashboard",
              icon: Component,
              href: newFrontendURL(TSP_CONFIG, "/", true),
              defaultOpen: false,
            },
            {
              title: "Rankings",
              icon: PiRanking,
              iconOptions: "stroke-2",
              href: newFrontendURL(TSP_CONFIG, "/rankings/", true),
              defaultOpen: false,
            },
            {
              title: "Circuits",
              icon: Plane,
              href: newFrontendURL(TSP_CONFIG, "/circuits/", true),
              defaultOpen: false,
              subSubSections: [
                {
                  title: "World Cup",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/WC/", true),
                },
                {
                  title: "NorAm",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/NAC/", true),
                },
                {
                  title: "Europa Cup",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/EC/", true),
                },
                {
                  title: "Nat. Champs",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/NC/", true),
                },
                {
                  title: "Open Cup",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/OPN/", true),
                },
                {
                  title: "World Champs",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/WSC/", true),
                },
                {
                  title: "World Jr. Champs",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/WJC/", true),
                },
                {
                  title: "Australian Nat. Cup",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/ANC/", true),
                },
                {
                  title: "University Games",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/UVS/", true),
                },
                {
                  title: "FIS Cup",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/FIS/", true),
                },
                {
                  title: "Asian Cup",
                  href: newFrontendURL(TSP_CONFIG, "/circuits/AC/", true),
                },
              ],
            },
            {
              title: "Hall of fame",
              icon: Medal,
              href: newFrontendURL(TSP_CONFIG, "/hall-of-fame/", true),
              defaultOpen: false,
              subSubSections: [
                {
                  title: "All time",
                  href: newFrontendURL(TSP_CONFIG, "/hall-of-fame/ALL/", true),
                },
                ...ALL_SEASONS.map((season) => ({
                  title: season,
                  href: newFrontendURL(
                    TSP_CONFIG,
                    `/hall-of-fame/${season}/`,
                    true,
                  ),
                })),
              ],
            },
          ],
        },
        {
          title: "Duals",
          subSections: [
            {
              title: "Events",
              icon: Swords,
              href: newFrontendURL(TSP_CONFIG, "/duals/events/", true),
              defaultOpen: false,
            },
            {
              title: "Matchups",
              icon: Crosshair,
              href: newFrontendURL(TSP_CONFIG, "/duals/matchups/", true),
              defaultOpen: false,
            },
          ],
        },
        {
          title: "Fantasy",
          subSections: [
            {
              title: "Play",
              icon: Trophy,
              href: newFrontendURL(TSP_CONFIG, "/fantasy/", true),
              defaultOpen: false,
            },
            {
              title: "Bets",
              icon: Dices,
              href: newFrontendURL(TSP_CONFIG, "/bets/", true),
              defaultOpen: false,
            },
            {
              title: "Leaderboard",
              icon: Medal,
              href: newFrontendURL(TSP_CONFIG, "/leaderboard/", true),
              defaultOpen: false,
            },
          ],
        },
        {
          title: "Athlete",
          subSections: [
            {
              title: "Career",
              icon: ChartNoAxesCombined,
              href: newFrontendURL(TSP_CONFIG, "/career/", true),
              defaultOpen: false,
            },
            {
              title: "Ski",
              icon: ChartNetwork,
              href: newFrontendURL(TSP_CONFIG, "/ski-analysis/", true),
              defaultOpen: false,
            },
            {
              title: "Jumps",
              icon: TbSkiJumping,
              href: newFrontendURL(TSP_CONFIG, "/athlete-jumps/top", true),
              defaultOpen: false,
              subSubSections: [
                {
                  title: "Top air",
                  href: newFrontendURL(TSP_CONFIG, "/athlete-jumps/top", true),
                },
                {
                  title: "Bottom air",
                  href: newFrontendURL(
                    TSP_CONFIG,
                    "/athlete-jumps/bottom",
                    true,
                  ),
                },
              ],
            },
            {
              title: "Races",
              icon: ClipboardList,
              href: newFrontendURL(TSP_CONFIG, "/athlete-races/", true),
              defaultOpen: false,
            },
          ],
        },
        {
          title: "Coach",
          subSections: [
            {
              title: "Air Analysis",
              icon: PieChart,
              href: newFrontendURL(TSP_CONFIG, "/air-analysis/", true),
              defaultOpen: false,
            },
            {
              title: "Judge Analysis",
              icon: Gauge,
              href: newFrontendURL(TSP_CONFIG, "/judge-analysis/", true),
              defaultOpen: false,
            },
            {
              title: "Calculator",
              icon: Calculator,
              href: newFrontendURL(TSP_CONFIG, "/calculator/", true),
              defaultOpen: false,
            },
          ],
        },
        ...(isUsa
          ? [
              {
                title: "USA",
                subSections: [
                  {
                    title: "Team USA",
                    icon: LiaFlagUsaSolid,
                    href: newFrontendURL(
                      TSP_CONFIG,
                      "/custom/usa/selections/",
                      true,
                    ),
                    defaultOpen: true,
                    subSubSections: [
                      {
                        title: "Selections",
                        icon: Scan,
                        href: newFrontendURL(
                          TSP_CONFIG,
                          "/custom/usa/selections/",
                          true,
                        ),
                      },
                    ],
                  },
                ],
              },
            ]
          : []),
        ...(isStaff
          ? [
              {
                title: "Staff",
                subSections: [
                  {
                    title: "Newsletters",
                    icon: Newspaper,
                    href: newFrontendURL(TSP_CONFIG, "/newsletter/", true),
                    defaultOpen: false,
                  },
                  {
                    title: "Emails",
                    icon: Mail,
                    href: newFrontendURL(TSP_CONFIG, "/emails/", true),
                    defaultOpen: false,
                  },
                  {
                    title: "Moguls logs",
                    icon: ClipboardList,
                    href: newFrontendURL(TSP_CONFIG, "/moguls-logs/", true),
                    defaultOpen: false,
                  },
                  {
                    title: "Accounts",
                    icon: Users,
                    href: newFrontendURL(TSP_CONFIG, "/accounts/", true),
                    defaultOpen: false,
                  },
                  {
                    title: "Users",
                    icon: UserCog,
                    href: newFrontendURL(TSP_CONFIG, "/staff/users/", true),
                    defaultOpen: false,
                  },
                ],
              },
            ]
          : []),
      ],
      footer: {
        showSettings: true,
        ...(isPro ? {} : { extra: <UpgradeSidebarCard /> }),
      },
    }
  }, [favorites, isUsa, isPro, isStaff])

  const defaultHead = useMemo(() => {
    const first = favorites[0]
    if (first) {
      return {
        id: first.id,
        title: `${first.last_name} ${first.first_name}`,
        subtitle: first.country,
        icon: (
          <CountryFlag
            code={first.country}
            className="h-8 w-8 rounded-full object-cover"
          />
        ),
      }
    }
    return {
      id: 0,
      title: "No favorites yet",
      subtitle: "Pick athletes to follow",
      icon: <UserIcon className="h-6 w-6" />,
    }
  }, [favorites])

  useEffect(() => {
    async function checkIfUserNull() {
      if (user === null) {
        await signOut()
      }
    }
    checkIfUserNull()
  }, [user])

  return (
    <AppSideBar
      config={TSP_CONFIG}
      settings={settings}
      headState={defaultHead}
      user={user}
    >
      {children}
    </AppSideBar>
  )
}
